/**
 * Admin: List, create, edit, and delete CMS pages.
 *
 * Navigation model:
 *   list   → default view; shows page table + "New page" button
 *   create → full-view form for a new page; back button returns to list
 *   edit   → full-view form for an existing page; back button returns to list
 */

import { useEffect, useMemo, useState, useRef } from "react";
import Editor from "@monaco-editor/react";
import {
	Plus,
	Pencil,
	Trash2,
	Eye,
	EyeOff,
	Send,
	X,
	Braces,
	ArrowLeft,
	FileText,
	Search,
	ChevronLeft,
	ChevronRight,
	Sparkles,
	Info,
} from "lucide-react";
import {
	getAllCMSPages,
	getCMSPageBySlugForAdmin,
	createCMSPage,
	updateCMSPage,
	deleteCMSPage,
	type CMSPage,
	type CMSPagePayload,
	type CMSPageType,
	type CMSPageStatus,
	type AIConfig,
} from "@/lib/cms";
import { getAIConfigForWorkflow } from "@/lib/ai-config";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { CMSBodyRenderer } from "../CMSBodyRenderer";
import { AdminCmsPlaceholderGuide } from "./AdminCmsPlaceholderGuide";
import { AdminAIPageCreator } from "./AdminAIPageCreator";
import { CMSWysiwygEditor } from "./CMSWysiwygEditor";
import {
	CMS_PAGE_LAYOUT_TEMPLATES,
	getCMSPageLayoutTemplate,
	type CMSPageLayoutTemplate,
} from "@/lib/cms-editor-blocks";
import { Tooltip } from "../ui/Tooltip";

/** Hover tooltip copy — clarifies Type vs layout template for admins */
const CMS_PAGE_TYPE_TOOLTIP =
	"This category is saved on the page and used by the site: Home can be chosen as the homepage; Landing feeds landing-page flows; Legal suits policies/terms; Generic is standard content. This is separate from “layout template,” which only fills starter HTML when you create a page.";
const CMS_LAYOUT_TEMPLATE_TOOLTIP =
	"When creating a new page only: choose starter HTML for the body, or blank for no scaffold. You can edit everything later. Applying a template may suggest defaults like Type or title visibility — adjust those anytime.";

type View = "list" | "create" | "edit";
type EditorMode = "visual" | "html";

interface PreviewPage {
	title: string;
	body: string;
	showTitle?: boolean;
}

interface AdminCMSPagesProps {
	currentUserId: string;
	showNotify?: (message: string, type?: "success" | "error" | "info") => void;
	confirmAction?: (title: string, message: string, action: () => void) => void;
	/** Optional action (e.g. Help & guides) to show in the title row. */
	titleAction?: React.ReactNode;
}

const EMPTY_FORM: CMSPagePayload = {
	slug: "",
	title: "",
	body: "",
	type: "generic",
	status: "draft",
	showTitle: true,
};

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

export function AdminCMSPages({
	currentUserId,
	showNotify,
	confirmAction,
	titleAction,
}: AdminCMSPagesProps): JSX.Element {
	const [pages, setPages] = useState<CMSPage[]>([]);
	const [loading, setLoading] = useState(true);
	const [view, setView] = useState<View>("list");
	const [editing, setEditing] = useState<CMSPage | null>(null);
	const [previewPage, setPreviewPage] = useState<PreviewPage | null>(null);
	const [form, setForm] = useState<CMSPagePayload>(EMPTY_FORM);
	const [selectedLayoutTemplate, setSelectedLayoutTemplate] = useState<
		CMSPageLayoutTemplate | ""
	>("");
	const [editorMode, setEditorMode] = useState<EditorMode>("visual");
	const editorRef = useRef<{
		getAction: (id: string) => { run: () => Promise<unknown> } | null;
	} | null>(null);

	const [aiConfig, setAIConfig] = useState<AIConfig | null>(null);
	const [showAICreator, setShowAICreator] = useState(false);

	/* List filter and pagination */
	const [searchQuery, setSearchQuery] = useState("");
	const [filterType, setFilterType] = useState<CMSPageType | "">("");
	const [filterStatus, setFilterStatus] = useState<CMSPageStatus | "">("");
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

	const load = async () => {
		setLoading(true);
		try {
			const list = await getAllCMSPages();
			setPages(list);
		} catch (err) {
			console.error("[AdminCMSPages] Load error:", err);
			showNotify?.("Failed to load pages.", "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		void load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		getAIConfigForWorkflow("cms_page_coding")
			.then((cfg) => setAIConfig(cfg))
			.catch((err) => console.error("[AdminCMSPages] Failed to load AI config:", err));
	}, []);

	const goToCreate = () => {
		setEditing(null);
		setForm(EMPTY_FORM);
		setSelectedLayoutTemplate("");
		setEditorMode("visual");
		setView("create");
	};

	const goToEdit = (p: CMSPage) => {
		setEditing(p);
		setForm({
			slug: p.slug,
			title: p.title,
			body: p.body,
			type: p.type,
			status: p.status,
			showTitle: p.showTitle !== false,
			order: p.order ?? undefined,
			path: p.path ?? undefined,
		});
		setSelectedLayoutTemplate("");
		setEditorMode("visual");
		setView("edit");
	};

	const goToList = () => {
		setView("list");
		setEditing(null);
		setForm(EMPTY_FORM);
		setSelectedLayoutTemplate("");
	};

	const applyLayoutTemplate = (templateId: CMSPageLayoutTemplate): void => {
		const template = getCMSPageLayoutTemplate(templateId);
		if (!template) return;

		setForm((prev) => ({
			...prev,
			body: template.html,
			type: template.defaultType ?? prev.type,
			showTitle: template.defaultShowTitle ?? prev.showTitle,
		}));
		showNotify?.(`Applied "${template.label}" layout template.`, "success");
	};

	const handleTemplateSelect = (value: string): void => {
		const templateId = value as CMSPageLayoutTemplate | "";
		setSelectedLayoutTemplate(templateId);
		if (view !== "create") return;
		if (!templateId) {
			setForm((prev) => ({ ...prev, body: "" }));
			showNotify?.("Cleared starter layout. Editor reset to blank.", "success");
			return;
		}

		const hasExistingBody = form.body.trim().length > 0;
		if (!hasExistingBody) {
			applyLayoutTemplate(templateId);
		}
	};

	const slugNormalized = form.slug.trim()
		? form.slug.trim().startsWith("/")
			? form.slug.trim()
			: `/${form.slug.trim()}`
		: "";
	const slugInvalid =
		form.slug.trim() !== "" && !form.slug.trim().startsWith("/");
	const fullUrlPreview =
		typeof window !== "undefined" && slugNormalized
			? `${window.location.origin}${slugNormalized}`
			: "";

	const openPreviewFromForm = () => {
		setPreviewPage({
			title: form.title || form.slug || "Untitled page",
			body: form.body || "",
			showTitle: form.showTitle,
		});
	};

	const handleSave = async () => {
		if (slugInvalid) {
			showNotify?.("Slug must start with /.", "error");
			return;
		}
		const slugToSave = slugNormalized || form.slug.trim() || "/";
		if (!slugToSave || slugToSave === "/") {
			showNotify?.("Please enter a slug (e.g. /about-us).", "error");
			return;
		}
		try {
			const existingWithSlug = await getCMSPageBySlugForAdmin(slugToSave);
			if (existingWithSlug && existingWithSlug.id !== editing?.id) {
				showNotify?.(
					`Slug "${slugToSave}" is already used by another page. Slugs must be unique.`,
					"error",
				);
				return;
			}
		} catch (err) {
			console.error("[AdminCMSPages] Slug uniqueness check failed:", err);
			showNotify?.("Could not verify slug. Try again.", "error");
			return;
		}
		const payload = {
			...form,
			slug: slugToSave,
		};
		try {
			if (editing) {
				await updateCMSPage(editing.id, payload, currentUserId);
				showNotify?.("Page updated.", "success");
				// Stay on edit page; refresh list in background for when user navigates back
				void load();
			} else {
				await createCMSPage(payload, currentUserId);
				showNotify?.("Page created.", "success");
				goToList();
				void load();
			}
		} catch (err) {
			console.error("[AdminCMSPages] Save error:", err);
			showNotify?.("Failed to save page.", "error");
		}
	};

	const handleDelete = (p: CMSPage) => {
		confirmAction?.(
			"Delete page?",
			`Delete "${p.title}"? This cannot be undone.`,
			async () => {
				try {
					await deleteCMSPage(p.id);
					showNotify?.("Page deleted.", "success");
					goToList();
					void load();
				} catch (err) {
					console.error("[AdminCMSPages] Delete error:", err);
					showNotify?.("Failed to delete page.", "error");
				}
			},
		);
	};

	const handleUnpublish = async (p: CMSPage) => {
		try {
			await updateCMSPage(p.id, { status: "draft" }, currentUserId);
			showNotify?.(`"${p.title}" unpublished.`, "success");
			void load();
		} catch (err) {
			console.error("[AdminCMSPages] Unpublish error:", err);
			showNotify?.("Failed to unpublish page.", "error");
		}
	};

	const handlePublish = async (p: CMSPage) => {
		try {
			await updateCMSPage(p.id, { status: "published" }, currentUserId);
			showNotify?.(`"${p.title}" published.`, "success");
			void load();
		} catch (err) {
			console.error("[AdminCMSPages] Publish error:", err);
			showNotify?.("Failed to publish page.", "error");
		}
	};

	/* Filtered and paginated list (list view only) */
	const filteredPages = useMemo(() => {
		let list = pages;
		const q = searchQuery.trim().toLowerCase();
		if (q) {
			list = list.filter(
				(p) =>
					p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q),
			);
		}
		if (filterType) list = list.filter((p) => p.type === filterType);
		if (filterStatus) list = list.filter((p) => p.status === filterStatus);
		return list;
	}, [pages, searchQuery, filterType, filterStatus]);

	const totalFiltered = filteredPages.length;
	const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));

	const paginatedPages = useMemo(() => {
		const start = (currentPage - 1) * pageSize;
		return filteredPages.slice(start, start + pageSize);
	}, [filteredPages, currentPage, pageSize]);

	/* Reset to page 1 when filters change */
	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, filterType, filterStatus, pageSize]);

	/* ── Preview modal ──────────────────────────────────────────────────────── */
	const PreviewModal = () =>
		previewPage ? (
			<div
				className="fixed inset-0 z-[60] flex flex-col bg-gray-50"
				role="dialog"
				aria-modal="true"
				aria-label={`Preview: ${previewPage.title}`}
			>
				<button
					type="button"
					onClick={() => setPreviewPage(null)}
					className="absolute top-3 right-3 z-[70] rounded-full bg-black/50 p-2 text-white hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white/80"
					aria-label="Close preview"
				>
					<X size={20} />
				</button>
				<div className="flex-1 overflow-y-auto min-h-0 flex flex-col w-full h-full">
					<Header
						user={null}
						userProfile={null}
						onProfileClick={() => {}}
						branding={{
							brandName: "GSGO",
							brandTagline: "Community support for CKD patients and families",
						}}
						isAdmin={false}
						linksDisabled
					/>
					<main className="flex-1 bg-white w-full" id="main-content">
						<article className="w-full">
							{previewPage.showTitle !== false && (
								<h1 className="text-3xl font-bold text-gray-900 mb-6">
									{previewPage.title}
								</h1>
							)}
							<CMSBodyRenderer
								body={previewPage.body || ""}
								className="prose prose-gray max-w-none w-full"
							/>
						</article>
					</main>
					<Footer
						brandName="GSGO"
						brandTagline="Community support for CKD patients and families"
						linksDisabled
					/>
				</div>
			</div>
		) : null;

	/* ── List view ──────────────────────────────────────────────────────────── */
	if (view === "list") {
		return (
			<div className="space-y-5">
				<PreviewModal />

				{/* Header row */}
				<div className="flex items-center justify-between gap-4">
					<div>
						<h2 className="text-xl font-bold text-gray-900">CMS Pages</h2>
						<p className="text-sm text-gray-500 mt-0.5">
							{loading
								? "Loading…"
								: totalFiltered === pages.length
									? `${pages.length} page${pages.length !== 1 ? "s" : ""}`
									: `${totalFiltered} of ${pages.length} page${pages.length !== 1 ? "s" : ""}`}
						</p>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						<button
							type="button"
							onClick={goToCreate}
							className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
						>
							<Plus size={16} />
							New page
						</button>
						{titleAction}
					</div>
				</div>

				{/* Filters */}
				{!loading && pages.length > 0 && (
					<div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
						<div className="relative flex-1 min-w-[180px] max-w-xs">
							<Search
								size={16}
								className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
								aria-hidden
							/>
							<input
								type="search"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search title or slug…"
								className="w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
								aria-label="Search pages by title or slug"
							/>
						</div>
						<select
							value={filterType}
							onChange={(e) =>
								setFilterType((e.target.value || "") as CMSPageType | "")
							}
							className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
							aria-label="Filter by type"
						>
							<option value="">All types</option>
							<option value="home">Home</option>
							<option value="landing">Landing</option>
							<option value="legal">Legal</option>
							<option value="generic">Generic</option>
						</select>
						<select
							value={filterStatus}
							onChange={(e) =>
								setFilterStatus((e.target.value || "") as CMSPageStatus | "")
							}
							className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
							aria-label="Filter by status"
						>
							<option value="">All statuses</option>
							<option value="draft">Draft</option>
							<option value="published">Published</option>
						</select>
					</div>
				)}

				{/* Page table */}
				<div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
					{loading ? (
						<div className="px-4 py-12 text-center text-gray-500 text-sm">
							Loading pages…
						</div>
					) : pages.length === 0 ? (
						<div className="flex flex-col items-center gap-3 px-4 py-16 text-center">
							<FileText size={36} className="text-gray-300" />
							<p className="text-gray-500 text-sm">No pages yet.</p>
							<button
								type="button"
								onClick={goToCreate}
								className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
							>
								<Plus size={14} />
								Create your first page
							</button>
						</div>
					) : totalFiltered === 0 ? (
						<div className="px-4 py-12 text-center text-gray-500 text-sm">
							No pages match the current filters. Try changing the search or
							filters.
						</div>
					) : (
						<>
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
											Title
										</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
											Slug
										</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
											Type
										</th>
										<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
											Status
										</th>
										<th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
											Actions
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-100 bg-white">
									{paginatedPages.map((p) => (
										<tr
											key={p.id}
											className="hover:bg-gray-50 transition-colors"
										>
											<td className="px-4 py-3 text-sm font-medium text-gray-900">
												{p.title}
											</td>
											<td className="px-4 py-3 text-sm text-gray-500 font-mono">
												{p.slug}
											</td>
											<td className="px-4 py-3">
												<span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 capitalize">
													{p.type}
												</span>
											</td>
											<td className="px-4 py-3">
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
														p.status === "published"
															? "bg-green-100 text-green-700"
															: "bg-yellow-100 text-yellow-700"
													}`}
												>
													{p.status}
												</span>
											</td>
											<td className="px-4 py-3 text-right">
												<div className="inline-flex items-center gap-1">
													<button
														type="button"
														onClick={() => setPreviewPage(p)}
														className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
														aria-label="Preview"
														title="Preview"
													>
														<Eye size={15} />
													</button>
													{p.status === "published" ? (
														<button
															type="button"
															onClick={() => {
																if (confirmAction) {
																	confirmAction(
																		"Unpublish page?",
																		`Unpublish "${p.title}"? The page will no longer be visible to visitors.`,
																		() => {
																			void handleUnpublish(p);
																		},
																	);
																} else {
																	void handleUnpublish(p);
																}
															}}
															className="rounded p-1.5 text-gray-400 hover:bg-amber-50 hover:text-amber-700 transition-colors"
															aria-label="Unpublish"
															title="Unpublish (set to draft)"
														>
															<EyeOff size={15} />
														</button>
													) : (
														<button
															type="button"
															onClick={() => {
																if (confirmAction) {
																	confirmAction(
																		"Publish page?",
																		`Publish "${p.title}"? The page will be visible to visitors.`,
																		() => {
																			void handlePublish(p);
																		},
																	);
																} else {
																	void handlePublish(p);
																}
															}}
															className="rounded p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-700 transition-colors"
															aria-label="Publish"
															title="Publish"
														>
															<Send size={15} />
														</button>
													)}
													<button
														type="button"
														onClick={() => goToEdit(p)}
														className="rounded p-1.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
														aria-label="Edit"
														title="Edit"
													>
														<Pencil size={15} />
													</button>
													<button
														type="button"
														onClick={() => handleDelete(p)}
														className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
														aria-label="Delete"
														title="Delete"
													>
														<Trash2 size={15} />
													</button>
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{/* Pagination */}
							{totalPages > 1 || pageSize !== DEFAULT_PAGE_SIZE ? (
								<div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50/60 px-4 py-3">
									<div className="flex items-center gap-2">
										<span className="text-sm text-gray-600">Per page</span>
										<select
											value={pageSize}
											onChange={(e) => setPageSize(Number(e.target.value))}
											className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
											aria-label="Items per page"
										>
											{PAGE_SIZE_OPTIONS.map((n) => (
												<option key={n} value={n}>
													{n}
												</option>
											))}
										</select>
									</div>
									<div className="flex items-center gap-2">
										<span className="text-sm text-gray-600">
											Page {currentPage} of {totalPages}
										</span>
										<div className="flex items-center gap-0.5">
											<button
												type="button"
												onClick={() =>
													setCurrentPage((c) => Math.max(1, c - 1))
												}
												disabled={currentPage <= 1}
												className="rounded border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
												aria-label="Previous page"
											>
												<ChevronLeft size={18} />
											</button>
											<button
												type="button"
												onClick={() =>
													setCurrentPage((c) => Math.min(totalPages, c + 1))
												}
												disabled={currentPage >= totalPages}
												className="rounded border border-gray-300 bg-white p-1.5 text-gray-600 hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
												aria-label="Next page"
											>
												<ChevronRight size={18} />
											</button>
										</div>
									</div>
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
		);
	}

	/* ── Create / Edit view ─────────────────────────────────────────────────── */
	return (
		<div className="fixed inset-0 left-0 lg:left-56 z-30 flex flex-col bg-white">
			<PreviewModal />

			{/* AI Page Creator modal */}
			{showAICreator && aiConfig && (
				<AdminAIPageCreator
					aiConfig={aiConfig}
					onAccept={(html) => {
						setForm((f) => ({ ...f, body: html }));
						showNotify?.("AI-generated HTML loaded into the editor.", "success");
					}}
					onClose={() => setShowAICreator(false)}
				/>
			)}

			{/* Two-column body */}
			<div className="flex flex-1 min-h-0 overflow-hidden">
				{/* Left sidebar: page metadata */}
				<aside className="w-72 shrink-0 flex flex-col gap-4 border-r border-gray-200 bg-gray-50 overflow-y-auto px-3 pt-2 pb-4 sm:px-4">
					<div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm space-y-3">
						<button
							type="button"
							onClick={goToList}
							className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
						>
							<ArrowLeft size={15} />
							Back to pages
						</button>
						<div className="border-t border-gray-100 pt-3">
							<p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
								{view === "edit" ? "Editing page" : "Creating page"}
							</p>
							<p className="mt-1 text-sm font-semibold text-gray-900 break-words">
								{view === "edit" && editing
									? editing.title || editing.slug
									: form.title || "New page"}
							</p>
						</div>
						<div className="grid grid-cols-2 gap-2">
							<button
								type="button"
								onClick={openPreviewFromForm}
								className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
							>
								<Eye size={14} />
								Preview
							</button>
							<button
								type="button"
								onClick={handleSave}
								className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
							>
								{view === "edit" ? "Update" : "Create"}
							</button>
						</div>
					</div>

					<div>
						<label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
							Slug (URL) — must be unique
						</label>
						<input
							type="text"
							value={form.slug}
							onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
							className={`w-full rounded-lg border px-3 py-2 text-sm bg-white shadow-sm transition-colors ${
								slugInvalid
									? "border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-500"
									: "border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
							}`}
							placeholder="/home-1"
							aria-invalid={slugInvalid}
							aria-describedby={
								slugInvalid
									? "slug-error"
									: fullUrlPreview
										? "slug-preview"
										: undefined
							}
						/>
						{slugInvalid && (
							<p id="slug-error" className="mt-1.5 text-xs text-red-600">
								Must start with{" "}
								<code className="rounded bg-red-50 px-1 font-mono">/</code>
							</p>
						)}
						{fullUrlPreview && !slugInvalid && (
							<p
								id="slug-preview"
								className="mt-1.5 text-xs text-gray-400 break-all"
							>
								<a
									href={fullUrlPreview}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-500 hover:underline"
								>
									{fullUrlPreview}
								</a>
							</p>
						)}
					</div>

					<div>
						<label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
							Title
						</label>
						<input
							type="text"
							value={form.title}
							onChange={(e) =>
								setForm((f) => ({ ...f, title: e.target.value }))
							}
							className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
						/>
					</div>

					<div>
						<div className="mb-1.5 flex items-center gap-1.5">
							<label
								htmlFor="cms-form-page-type"
								className="text-xs font-semibold text-gray-500 uppercase tracking-wide"
							>
								Type
							</label>
							<Tooltip
								content={CMS_PAGE_TYPE_TOOLTIP}
								position="top"
								maxWidth="340px"
							>
								<button
									type="button"
									className="rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
									aria-label="Explain page type"
								>
									<Info size={14} className="shrink-0" aria-hidden />
								</button>
							</Tooltip>
						</div>
						<select
							id="cms-form-page-type"
							value={form.type}
							onChange={(e) =>
								setForm((f) => ({ ...f, type: e.target.value as CMSPageType }))
							}
							className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
						>
							<option value="home">Home</option>
							<option value="landing">Landing</option>
							<option value="legal">Legal</option>
							<option value="generic">Generic</option>
						</select>
					</div>

					{view === "create" && (
						<div>
							<div className="mb-1.5 flex items-center gap-1.5">
								<label
									htmlFor="cms-form-layout-template"
									className="text-xs font-semibold text-gray-500 uppercase tracking-wide"
								>
									Layout template
								</label>
								<Tooltip
									content={CMS_LAYOUT_TEMPLATE_TOOLTIP}
									position="top"
									maxWidth="340px"
								>
									<button
										type="button"
										className="rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
										aria-label="Explain layout template"
									>
										<Info size={14} className="shrink-0" aria-hidden />
									</button>
								</Tooltip>
							</div>
							<div className="space-y-2">
								<select
									id="cms-form-layout-template"
									value={selectedLayoutTemplate}
									onChange={(e) => handleTemplateSelect(e.target.value)}
									className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
								>
									<option value="">Blank page (no starter layout)</option>
									{CMS_PAGE_LAYOUT_TEMPLATES.map((template) => (
										<option key={template.id} value={template.id}>
											{template.label}
										</option>
									))}
								</select>
								{selectedLayoutTemplate && (
									<>
										<p className="text-xs text-gray-500">
											{
												getCMSPageLayoutTemplate(selectedLayoutTemplate)
													?.description
											}
										</p>
										<button
											type="button"
											onClick={() =>
												applyLayoutTemplate(selectedLayoutTemplate)
											}
											className="w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors"
										>
											Apply selected template to body
										</button>
									</>
								)}
							</div>
						</div>
					)}

					<div>
						<label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
							Status
						</label>
						<select
							value={form.status}
							onChange={(e) =>
								setForm((f) => ({
									...f,
									status: e.target.value as CMSPageStatus,
								}))
							}
							className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
						>
							<option value="draft">Draft</option>
							<option value="published">Published</option>
						</select>
					</div>

					<div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
						<input
							type="checkbox"
							id="show-title"
							checked={form.showTitle !== false}
							onChange={(e) =>
								setForm((f) => ({ ...f, showTitle: e.target.checked }))
							}
							className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
						/>
						<label
							htmlFor="show-title"
							className="text-sm font-medium text-gray-700 cursor-pointer select-none"
						>
							Show title on page
						</label>
					</div>

					<AdminCmsPlaceholderGuide
						title="Guide: embed codes in body"
						context="In the body HTML you can use {{phone-number}}, {{contact-email}}, etc.; they are replaced with values from Settings & Branding."
						className="mt-2"
					/>

					{/* Delete button for edit view */}
					{view === "edit" && editing && (
						<div className="mt-auto pt-4 border-t border-gray-200">
							<button
								type="button"
								onClick={() => handleDelete(editing)}
								className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors"
							>
								<Trash2 size={14} />
								Delete page
							</button>
						</div>
					)}
				</aside>

			{/* Right: Body editor (Visual + HTML) */}
			<div
				className={`flex flex-col flex-1 min-w-0 min-h-0 ${
					editorMode === "html" ? "bg-[#1e1e1e]" : "bg-white"
				}`}
			>
				<div
					className={`flex items-center justify-between gap-3 px-4 py-2 border-b shrink-0 ${
						editorMode === "html"
							? "bg-[#252526] border-[#3c3c3c]"
							: "bg-gray-50 border-gray-200"
					}`}
				>
					<div className="flex items-center gap-2">
						<span
							className={`text-xs font-medium tracking-wide uppercase ${
								editorMode === "html" ? "text-gray-400" : "text-gray-500"
							}`}
						>
							Body ({editorMode === "visual" ? "Visual" : "HTML"})
						</span>
						<div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
							<button
								type="button"
								onClick={() => setEditorMode("visual")}
								className={`px-2.5 py-1 text-xs font-medium transition-colors ${
									editorMode === "visual"
										? "bg-blue-600 text-white"
										: "bg-white text-gray-600 hover:bg-gray-50"
								}`}
							>
								Visual
							</button>
							<button
								type="button"
								onClick={() => setEditorMode("html")}
								className={`px-2.5 py-1 text-xs font-medium transition-colors ${
									editorMode === "html"
										? "bg-blue-600 text-white"
										: "bg-white text-gray-600 hover:bg-gray-50"
								}`}
							>
								HTML
							</button>
						</div>
					</div>
					<div className="flex items-center gap-1.5">
						<button
							type="button"
							onClick={() => {
								if (!aiConfig) {
									showNotify?.(
										"AI not configured. Go to Settings → AI Content Generation to add an API key.",
										"info",
									);
									return;
								}
								setShowAICreator(true);
							}}
							className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
								editorMode === "html"
									? "border border-violet-500/40 bg-violet-600/20 text-violet-300 hover:bg-violet-600/30 hover:text-violet-200"
									: "border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
							}`}
							title="Generate page HTML with AI"
						>
							<Sparkles size={12} />
							Generate with AI
						</button>
						{editorMode === "html" && (
							<button
								type="button"
								onClick={() => {
									const action = editorRef.current?.getAction?.(
										"editor.action.formatDocument",
									);
									action?.run?.();
								}}
								className="inline-flex items-center gap-1.5 rounded border border-[#3c3c3c] bg-[#2d2d2d] px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-[#383838] hover:text-white transition-colors"
								title="Format document"
							>
								<Braces size={12} />
								Format
							</button>
						)}
					</div>
				</div>

				<div
					className={`flex-1 min-h-0 p-4 ${
						editorMode === "visual" ? "overflow-hidden" : "overflow-auto"
					}`}
				>
					{editorMode === "visual" ? (
						<CMSWysiwygEditor
							className="h-full"
							value={form.body}
							onChange={(html) => setForm((f) => ({ ...f, body: html }))}
							placeholder="Write your page content here…"
						/>
					) : (
						<div className="h-full min-h-[420px]">
							<Editor
								key={(view === "edit" ? editing?.id : "new") ?? "new"}
								height="100%"
								defaultLanguage="html"
								language="html"
								theme="vs-dark"
								value={form.body}
								onChange={(value) =>
									setForm((f) => ({ ...f, body: value ?? "" }))
								}
								onMount={(editor) => {
									editorRef.current =
										editor as unknown as typeof editorRef.current;
									if (form.body.trim()) {
										setTimeout(() => {
											editor
												.getAction?.("editor.action.formatDocument")
												?.run?.();
										}, 300);
									}
								}}
								options={{
									lineNumbers: "on",
									minimap: { enabled: true },
									folding: true,
									fontSize: 13,
									wordWrap: "on",
									scrollBeyondLastLine: false,
									padding: { top: 12 },
									tabSize: 2,
									automaticLayout: true,
								}}
								loading={<div className="w-full h-full bg-[#1e1e1e]" />}
							/>
						</div>
					)}
				</div>
			</div>
			</div>
		</div>
	);
}
